import { colors } from 'styles/colors';
import { HealthUnitsInfo } from '../contexts/HealthDataContext';
import { t } from 'i18next';
import { HealthMapCardInfo, ThermalComfortMapCardInfo } from '../Map/components/MapInfo/types';
import { ThermalComfortUnitsInfo } from '../contexts/ThermalComfortDataContext';
import { MapInfoProps } from '../Map/components/MapInfo';

const defaultHealthInfoValue = {
  total: 0,
  healthy: 0,
  critical: 0,
  alert: 0,
  inoperative: 0,
};

const defaultThermalComfortInfoValue: ThermalComfortMapCardInfo = {
  total: 0,
  comfortable: 0,
  regular: 0,
  bad: 0,
  inoperative: 0,
};

const formatLabel = (
  count: number,
  singular: string,
  plural?: string,
  lowerCase = true,
) => {
  const base = count === 1 || !plural ? singular : plural;
  return `${count} ${lowerCase ? base.toLowerCase() : base}`;
};

export function buildHealthInfoCardProps(scope: 'state' | 'unit', info: HealthMapCardInfo): {
  list: {
    label: string,
    items: {label: string, color: string}[]
  };
  total: { label: string, value: number };
} {
  const totalLabel = scope === 'state' ? t('totalUnidadesMonitoradas') : t('totalMaquinasMonitoradas');
  const listLabel = scope === 'state' ? t('resumoSaudeUnidades') : t('resumoSaudeMaquinas');

  return {
    total: {
      label: totalLabel,
      value: info.total,
    },
    list: {
      label: listLabel,
      items: [
        {
          color: colors.state.success.base,
          label: formatLabel(info.healthy, t('saudavel'), t('saudaveis')),
        },
        {
          color: colors.state.warning.base,
          label: formatLabel(info.alert, t('emAlertaSemiCritico')),
        },
        {
          color: colors.state.danger.base,
          label: formatLabel(info.critical, t('emAlertaCritico')),
        },
        {
          color: colors.icon['soft-400'],
          label: formatLabel(info.inoperative, t('offline')),
        },
      ],
    },
  };
}

export function buildThermalComfortInfoCardProps(scope: 'state' | 'unit', info: ThermalComfortMapCardInfo): {
  list: {
    label: string,
    items: {label: string, color: string}[]
  };
  total: { label: string, value: number };
} {
  const totalLabel = scope === 'state' ? t('totalUnidadesMonitoradas') : t('totalAmbientesMonitorados');
  const listLabel = t('resumoConfortoTermico');

  return {
    total: {
      label: totalLabel,
      value: info.total,
    },
    list: {
      label: listLabel,
      items: [
        {
          color: colors.state.success.base,
          label: formatLabel(info.comfortable, t('confortoBom'), undefined, false),
        },
        {
          color: colors.state.warning.base,
          label: formatLabel(info.regular, t('confortoRegular'), undefined, false),
        },
        {
          color: colors.state.danger.base,
          label: formatLabel(info.bad, t('confortoRuim'), undefined, false),
        },
        {
          color: colors.icon['soft-400'],
          label: formatLabel(info.inoperative, t('offline'), undefined, false),
        },
        ...(scope === 'unit' ? [{
          color: colors.Black,
          label: formatLabel(info.outsideOperatingHours ?? 0, t('foraHorarioFuncionamento'), undefined, false),
        }] : []),
      ],
    },
  };
}

export function buildInfoProps(props: MapInfoProps) {
  if (props.type === 'health') {
    return buildHealthInfoCardProps(props.scope, buildHealthyInfo(props.scope, props.unitsMap, props.name, props.state, props.unitInfo?.id));
  }
  return buildThermalComfortInfoCardProps(props.scope, buildThermalComfortInfo(props.scope, props.unitsMap, props.name, props.state, props.unitInfo?.id));
}

function buildHealthyInfoState(map: Map<string, HealthUnitsInfo>, state: string | undefined): HealthMapCardInfo {
  const stateUnits = map.get(state ?? '') ?? [];

  const counts = stateUnits.reduce(
    (acc, item) => {
      acc.total++;
      acc[item.health] += 1;
      return acc;
    },
    {
      total: 0,
      healthy: 0,
      critical: 0,
      alert: 0,
      inoperative: 0,
      exception: 0,
    },
  );

  return {
    alert: counts.alert,
    critical: counts.critical,
    healthy: counts.healthy,
    total: counts.total,
    inoperative: counts.inoperative,
  };
}

function buildHealthyInfoUnit(map: Map<string, HealthUnitsInfo>, state: string, unitId: number): HealthMapCardInfo {
  const unit = map.get(state)?.find((item) => item.id === unitId);

  if (!unit) {
    return defaultHealthInfoValue;
  }

  const total = Object.values(unit.machines).reduce((prev, current) => prev + current, 0);

  return {
    ...unit.machines,
    total,
  };
}

export function buildHealthyInfo(
  scope: 'state' | 'unit',
  map: Map<string, HealthUnitsInfo>,
  name?: string,
  state?: string,
  unitId?: number,
): HealthMapCardInfo {
  if (!name || (scope === 'unit' && (!state || !unitId))) {
    return defaultHealthInfoValue;
  }

  if (scope === 'state') return buildHealthyInfoState(map, name);

  if (scope === 'unit' && unitId) return buildHealthyInfoUnit(map, (state as string), unitId);

  throw new Error('Scope not found');
}

export function buildThermalComfortInfo(
  scope: 'state' | 'unit',
  map: Map<string, ThermalComfortUnitsInfo>,
  name?: string,
  state?: string,
  unitId?: number,
): ThermalComfortMapCardInfo {
  if (!name || (scope === 'unit' && (!state || !unitId))) {
    return defaultThermalComfortInfoValue;
  }

  if (scope === 'state') return buildThermalComfortInfoState(map, name);

  if (scope === 'unit' && unitId) return buildThermalComfortInfoUnit(map, (state as string), unitId);

  throw new Error('Scope not found');
}

function buildThermalComfortInfoState(map: Map<string, ThermalComfortUnitsInfo>, state: string | undefined): ThermalComfortMapCardInfo {
  const stateUnits = map.get(state ?? '') ?? [];

  const counts = stateUnits.reduce(
    (acc, item) => {
      acc.total++;
      acc[item.thermalComfort] += 1;
      return acc;
    },
    {
      total: 0,
      comfortable: 0,
      regular: 0,
      bad: 0,
      inoperative: 0,
    },
  );

  return {
    total: counts.total,
    comfortable: counts.comfortable,
    regular: counts.regular,
    bad: counts.bad,
    inoperative: counts.inoperative,
  };
}

function buildThermalComfortInfoUnit(map: Map<string, ThermalComfortUnitsInfo>, state: string, unitId: number): ThermalComfortMapCardInfo {
  const unit = map.get(state)?.find((item) => item.id === unitId);

  if (!unit) {
    return defaultThermalComfortInfoValue;
  }

  const total = Object.values(unit.environments).reduce((prev, current) => prev + current, 0);

  return {
    ...unit.environments,
    total,
  };
}
